import {PlanTimer} from './PlanTimer';
import {PlanResultCard} from './PlanResultCard';
import {PlanExerciseActions,PlanAddExercise,type PlanMeta} from './PlanSessionTools';
import './training-plan.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { flushSync } from 'react-dom';
import {
  ACTIVITY_TITLE,
  formatDayMonthShort,
  formatNumber,
  localNow,
  minutesBetween,
  parseNumericInput,
  type ActivityKind,
  type Exercise,
  type Workout,
  type WorkoutExercise,
} from '@jungles/shared';
import './workout-progress.css';
import { muscleGroupTitle } from '../lib/workoutTitle';
import {useOwnerApi} from '../lib/owner-api';
import {getSessionEpoch} from '../lib/api';
import { useApi, useAutosave } from '../lib/hooks';
import { useUser } from '../app/session';
import { useToast } from '../app/toast';
import { hapticSuccess } from '../lib/telegram';
import { ErrorState, Header, Loader, Screen, TabBar } from '../components/Chrome';
import { Dialog } from '../components/Sheet';
import { ChevronIcon, GymIcon } from '../components/icons';
import { ExercisePicker } from '../components/ExercisePicker';
import { GymPicker } from '../components/GymPicker';

/**
 * T1–T5 · Запись тренировки: своя, готовая и другая активность.
 * Один экран на три режима — отличается только состав полей (SPEC §5).
 */

const ACTIVITY_KINDS: ActivityKind[] = ['run', 'walk', 'bike', 'swim', 'other'];

type DraftExercise = Omit<WorkoutExercise, 'sets'> & {
  sets: (Omit<WorkoutExercise['sets'][number], 'weightKg' | 'reps'> & {
    weightKg: string;
    reps: string;
  })[];
};

function optionalNumber(text: string, integer = false): number | null {
  return text.trim() === '' ? null : parseNumericInput(text, integer);
}

function actualSetSummary(sets: DraftExercise['sets']): string {
  const count = sets.length;
  const reps = sets.map(set => optionalNumber(set.reps, true));
  const common = reps[0];
  if (count > 0 && common != null && Number.isInteger(common) && common >= 0 && common <= 1000 && reps.every(value => value === common)) {
    return `${count} × ${common}`;
  }
  const form = new Intl.PluralRules('ru').select(count);
  return `${count} ${form === 'one' ? 'подход' : form === 'few' ? 'подхода' : 'подходов'}`;
}

function validNumber(text: string, max: number, integer = false): boolean {
  const value = optionalNumber(text, integer);
  return value === null || (Number.isFinite(value) && value >= 0 && value <= max);
}

function validDraft(draft: Draft): boolean {
  return validNumber(draft.durationMin, 1440, true) && validNumber(draft.distanceKm, 1000) &&
    draft.exercises.every(exercise => exercise.sets.every(set =>
      validNumber(set.weightKg, 1000) && validNumber(set.reps, 1000, true)));
}

function timeOf(iso: string | null, timezone: string): string {
  if (!iso) return '';
  return localNow(timezone, new Date(iso)).time;
}

interface Draft {
  date: string;
  startTime: string;
  endTime: string;
  durationMin: string;
  gymId: string;
  /** Название держим в черновике: справочник с сервера, по id его не найти. */
  gymName: string;
  activityKind: ActivityKind;
  distanceKm: string;
  exercises: DraftExercise[];
}

function toDraft(workout: Workout, timezone: string): Draft {
  return {
    date: workout.date,
    startTime: timeOf(workout.startedAt, timezone),
    endTime: timeOf(workout.endedAt, timezone),
    durationMin: workout.durationMin === null ? '' : String(workout.durationMin),
    gymId: workout.gymId ?? '',
    gymName: workout.gymName ?? '',
    activityKind: workout.activityKind ?? 'run',
    distanceKm: workout.distanceKm === null ? '' : String(workout.distanceKm),
    exercises: workout.exercises.map(exercise => ({ ...exercise, sets: exercise.sets.map(set => ({
      ...set, weightKg: set.weightKg === null ? '' : String(set.weightKg),
      reps: set.reps === null ? '' : String(set.reps),
    })) })),
  };
}

function volumeOf(exercises: DraftExercise[]): number {
  return exercises.reduce(
    (total, exercise) =>
      total +
      exercise.sets.reduce(
        (sum, set) =>
          validNumber(set.weightKg, 1000) && validNumber(set.reps, 1000, true)
            ? sum + (optionalNumber(set.weightKg) ?? 0) * (optionalNumber(set.reps, true) ?? 0)
            : sum,
        0,
      ),
    0,
  );
}

export function WorkoutScreen() {
  const { id = '' } = useParams();const owner=useUser().id;
  // Route parameter changes reuse a screen unless its draft lifetime is keyed.
  return <WorkoutEditor key={owner+':'+getSessionEpoch()+':'+id} id={id} />;
}

function WorkoutEditor({ id }: { id: string }) {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const user = useUser();
  const api=useOwnerApi(user.id);
  const navigate = useNavigate();
  const toast = useToast();

  const { data: workout, error, loading, setData } = useApi<Workout & {trainingPlan?:boolean}>(`/api/workouts/${id}`);
  const { data: catalog } = useApi<Exercise[]>('/api/exercises');

  const [planLinked,setPlanLinked]=useState(false);
  useEffect(()=>{if(workout?.trainingPlan)setPlanLinked(true)},[workout]);
  const planned=workout?.trainingPlan===true||planLinked;
  const {data:planMeta,reload:reloadPlan}=useApi<PlanMeta>(planned?`/api/training-plan/session/${id}`:null);
  const [focused,setFocused]=useState(0);
  const [swiped,setSwiped]=useState<string|null>(null);
  const swipeStart=useRef<{id:string;x:number;y:number}|null>(null);
  const [wheel,setWheel]=useState<{exercise:number;set:number;field:'weightKg'|'reps';value:string;step:number}|null>(null);
  const [result,setResult]=useState<Workout|null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [picker, setPicker] = useState(false);
  const [gymPicker, setGymPicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [committing, setCommitting] = useState<'finish' | 'template' | 'delete' | 'leave' | null>(null);
  const commitRef = useRef<typeof committing>(null);
  const busy = committing !== null;

  const beginCommit = (action: NonNullable<typeof committing>) => {
    if (commitRef.current || !mounted.current) return false;
    // Commit native blur/change work before taking the snapshot boundary. Flush
    // React's queued draft updaters before autosave.flush reads its pending value.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    commitRef.current = action;
    flushSync(() => setCommitting(action));
    return true;
  };
  const endCommit = () => {
    if (!mounted.current) return;
    commitRef.current = null;
    setCommitting(null);
  };

  useEffect(() => {
    if (workout && !draft) setDraft(toDraft(workout, user.timezone));
    if(workout?.status==='done'&&new URLSearchParams(window.location.search).has('planResult'))setResult(workout);
  }, [workout, draft, user.timezone]);

  const persist = useCallback(
    async (value: Draft) => {
      // Invalid raw text stays editable, but must never become JSON null or a stale save.
      if (!validDraft(value)) throw new Error('Проверьте значения тренировки');
      const payload = {
        date: value.date,
        startTime: value.startTime || null,
        endTime: value.endTime || null,
        durationMin: optionalNumber(value.durationMin, true),
        gymId: value.gymId || null,
        activityKind: workout?.type === 'activity' ? value.activityKind : undefined,
        distanceKm: optionalNumber(value.distanceKm),
        exercises: value.exercises.map((exercise, index) => ({
          exerciseId: exercise.exerciseId,
          order: index + 1,
          restSec: exercise.restSec,
          targetSets: exercise.targetSets,
          targetReps: exercise.targetReps,
          sets: exercise.sets.map((set, setIndex) => ({
            order: setIndex + 1,
            weightKg: optionalNumber(set.weightKg),
            reps: optionalNumber(set.reps, true),
            done: set.done,
          })),
        })),
      };
      const updated = await api.patch<Workout>(`/api/workouts/${id}`, payload);
      if (mounted.current) setData(updated);
    },
    [id, setData, workout?.type],
  );

  const { schedule, flush, saving } = useAutosave(persist);

  const update = useCallback(
    (patch: Partial<Draft>) => {
      if (commitRef.current || !mounted.current) return;
      setDraft((current) => {
        if (!current) return current;
        const next = { ...current, ...patch };
        schedule(next);
        return next;
      });
    },
    [schedule],
  );

  const totalVolume = useMemo(() => (draft ? volumeOf(draft.exercises) : 0), [draft]);

  if (loading || !draft || !workout) {
    return (
      <Screen tabs>
        <Header />
        {error ? <ErrorState message={error} /> : <Loader />}
        <TabBar />
      </Screen>
    );
  }

  if(planMeta?.assessmentRequired)return <Screen className="training-plan"><h1>Проверим самочувствие перед продолжением</h1><p>Тренировка сохранена. Чтобы продолжить автоматический план, ответьте на текущие вопросы. Ручной дневник и история остаются доступны.</p><button className="b p" onClick={()=>navigate('/workouts/plan')}>К проверке самочувствия</button><button className="b" onClick={()=>navigate('/workouts')}>К ручному дневнику</button></Screen>;
  const isActivity = workout.type === 'activity';
  const isReady = workout.type === 'ready';
  const isDone = workout.status === 'done';
  const numericValid = validDraft(draft);

  const addExercise = (exercise: Exercise) => {
    setPicker(false);
    update({
      exercises: [
        ...draft.exercises,
        {
          id: `new-${exercise.id}-${draft.exercises.length}`,
          exerciseId: exercise.id,
          name: exercise.name,
          icon: exercise.icon,
          order: draft.exercises.length + 1,
          restSec: null,
          targetSets: null,
          targetReps: null,
          previous: null,
          sets: [{ id: 'new-1', order: 1, weightKg: '', reps: '', done: false }],
        },
      ],
    });
  };

  const removeExercise = (index: number) => {
    update({ exercises: draft.exercises.filter((_, i) => i !== index) });
  };

  const addSet = (index: number) => {
    const exercises = draft.exercises.map((exercise, i) => {
      if (i !== index) return exercise;
      const last = exercise.sets[exercise.sets.length - 1];
      return {
        ...exercise,
        sets: [
          ...exercise.sets,
          {
            id: `new-${exercise.sets.length + 1}-${Date.now()}`,
            order: exercise.sets.length + 1,
            weightKg: last?.weightKg ?? '',
            reps: last ? '' : (exercise.targetReps === null ? '' : String(exercise.targetReps)),
            done: false,
          },
        ],
      };
    });
    update({ exercises });
  };

  const patchSet = (
    exerciseIndex: number,
    setIndex: number,
    patch: Partial<{ weightKg: string; reps: string; done: boolean }>,
  ) => {
    const exercises = draft.exercises.map((exercise, i) =>
      i !== exerciseIndex
        ? exercise
        : {
            ...exercise,
            sets: exercise.sets.map((set, j) => (j !== setIndex ? set : { ...set, ...patch })),
          },
    );
    update({ exercises });
  };

  const removeSet = (exerciseIndex: number, setIndex: number) => {
    const exercises = draft.exercises.map((exercise, i) =>
      i !== exerciseIndex
        ? exercise
        : { ...exercise, sets: exercise.sets.filter((_, j) => j !== setIndex) },
    );
    update({ exercises });
  };

  const leave = async (to: '/workouts' | '/workouts/history' | '/') => {
    if (!numericValid || !beginCommit('leave')) return;
    try {
      await flush();
      if (!mounted.current) return;
      if(planned&&!isDone)await api.post('/api/training-plan/pause',{workoutId:id});
      if(mounted.current)navigate(to);
    } catch (err) {
      if (!mounted.current) return;
      toast(err instanceof Error ? err.message : 'Не удалось сохранить правки');
      endCommit();
    }
  };

  const finish = async () => {
    if (!numericValid || !beginCommit('finish')) return;
    try {
      await flush();
      if (!mounted.current) return;
      const finished=await api.post<Workout>(`/api/workouts/${id}/finish`);
      if (!mounted.current) return;
      hapticSuccess();
      toast('Тренировка записана');
      if(planned){setResult(finished);endCommit();}else navigate('/workouts/history', { replace: true });
    } catch (err) {
      if (!mounted.current) return;
      toast(err instanceof Error ? err.message : 'Не удалось завершить');
      endCommit();
    }
  };

  const saveAsTemplate = async () => {
    if (!numericValid || !beginCommit('template')) return;
    try {
      await flush();
      if (!mounted.current) return;
      await api.post('/api/templates', { workoutId: id });
      if (mounted.current) toast('Сохранили как шаблон');
    } catch (err) {
      if (!mounted.current) return;
      toast(err instanceof Error ? err.message : 'Не удалось сохранить шаблон');
    } finally {
      endCommit();
    }
  };

  const remove = async () => {
    if (!beginCommit('delete')) return;
    try {
      await api.del(`/api/workouts/${id}`);
      if (!mounted.current) return;
      toast(isDone ? 'Тренировка удалена' : 'Черновик удалён');
      navigate('/workouts', { replace: true });
    } catch (err) {
      if (!mounted.current) return;
      toast(err instanceof Error ? err.message : 'Не удалось удалить');
      endCommit();
      setConfirmDelete(false);
    }
  };

  const runPlanAction=(fn:()=>Promise<void>,refresh=false)=>{if(!beginCommit('leave'))return;void(async()=>{try{await flush();if(!mounted.current)return;await fn();if(refresh&&mounted.current){const w=await api.get<Workout>(`/api/workouts/${id}`);if(mounted.current){setData(w);setDraft(toDraft(w,user.timezone));reloadPlan();}}}catch(err){if(mounted.current)toast(err instanceof Error?err.message:'Не удалось сохранить. Повторите.')}finally{endCommit()}})()};
  if(result)return <Screen className="training-plan"><h1>Тренировка записана</h1><PlanResultCard workout={result}/><h2>Как прошло?</h2>{[['easy','Легко'],['normal','Нормально'],['hard','Тяжело'],['limit','На пределе']].map(([v,label])=><button key={v} disabled={busy} className="b" onClick={()=>runPlanAction(async()=>{await api.post('/api/training-plan/feedback',{workoutId:id,difficulty:v});toast('Самочувствие сохранено')})}>{label}</button>)}<p className="nt">В следующий раз: рост веса только после всех целевых подходов, известного запаса и проверки самочувствия. Иначе держим вес.</p><button className="b p big" disabled={busy} onClick={()=>navigate('/workouts/plan')}>Готово</button></Screen>;
  const duration =
    draft.startTime && draft.endTime ? minutesBetween(draft.startTime, draft.endTime) : null;

  return (
    <Screen tabs={!planned} className={planned?'training-plan tp-focused':undefined}>
      {!planned&&<Header />}
      {planned&&<div className="tp-focus-header"><button className="b ic" aria-label="Приостановить тренировку" disabled={busy||!numericValid} onClick={()=>void leave('/')}>×</button><span>{workout.title}<PlanTimer startedAt={workout.startedAt}/></span><button className="b" disabled={busy||!numericValid} onClick={()=>void finish()}>Завершить</button></div>}
      <button className="back" type="button" disabled={busy || !numericValid}
        onClick={() => void leave('/workouts')}>← Тренировки</button>

      {isReady ? <div className="eb">Готовая тренировка</div> : null}
      {!planned&&<h1>{workout.type === 'activity' ? workout.title : muscleGroupTitle(workout.title, draft.exercises, catalog)}</h1>}
      <p className="nt" style={{ margin: '0 0 12px' }}>
        {isDone
          ? `Запись сохранена${saving ? ' · сохраняем правки…' : ''}`
          : saving
            ? 'Сохраняем черновик…'
            : 'Черновик сохраняется автоматически'}
      </p>

      <fieldset disabled={busy} aria-busy={busy} aria-label="Редактирование тренировки"
        style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <section className={isActivity ? 'card' : 'card workout-details'}>
        {isActivity ? (
          <div className="fields">
            <label className="fl">
              Дата
              <input
                className="f"
                type="date"
                value={draft.date}
                onChange={(event) => update({ date: event.target.value })}
              />
            </label>
            <label className="fl">
              Начало
              <input
                className="f"
                type="time"
                value={draft.startTime}
                onChange={(event) => update({ startTime: event.target.value })}
              />
            </label>
            <label className="fl">
              Длительность, мин
              <input
                className="f"
                type="text"
                inputMode="numeric"
                aria-invalid={!validNumber(draft.durationMin, 1440, true)}
                value={draft.durationMin}
                onChange={(event) => update({ durationMin: event.target.value })}
              />
            </label>
            <label className="fl">
              Вид активности
              <select
                className="f"
                value={draft.activityKind}
                onChange={(event) =>
                  update({ activityKind: event.target.value as ActivityKind })
                }
              >
                {ACTIVITY_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {ACTIVITY_TITLE[kind]}
                  </option>
                ))}
              </select>
            </label>
            <label className="fl" style={{ gridColumn: '1 / -1' }}>
              Расстояние, км <span className="mu">· необязательно</span>
              <input
                className="f"
                type="text"
                inputMode="decimal"
                aria-invalid={!validNumber(draft.distanceKm, 1000)}
                value={draft.distanceKm}
                onChange={(event) => update({ distanceKm: event.target.value })}
              />
            </label>
          </div>
        ) : (
          <div className="workout-times">
            <label className="fl sm">
              Дата
              <input
                className="f sm"
                type="date"
                value={draft.date}
                onChange={(event) => update({ date: event.target.value })}
              />
            </label>
            <label className="fl sm">
              Начало
              <input
                className="f sm"
                type="time"
                value={draft.startTime}
                onChange={(event) => update({ startTime: event.target.value })}
              />
            </label>
            <label className="fl sm">
              Окончание
              <input
                className="f sm"
                type="time"
                value={draft.endTime}
                onChange={(event) => update({ endTime: event.target.value })}
              />
            </label>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <button className="gymf gbtn" type="button" onClick={() => setGymPicker(true)}>
            <GymIcon />
            <span className="gl">Зал</span>
            <span className="gval">
              {draft.gymName || 'Без зала · дома или на улице'}
              {draft.gymId && draft.gymId === user.homeGymId ? ' · мой зал' : ''}
            </span>
            <ChevronIcon />
          </button>
        </div>

        {duration !== null ? (
          <p className="nt" style={{ margin: '10px 2px 0' }}>
            Длительность: {duration} мин
            {totalVolume > 0 ? ` · объём ${formatNumber(Math.round(totalVolume))} кг` : ''}
          </p>
        ) : null}
      </section>

      {!isActivity ? (
        <>
          {isReady ? (
            <p className="nt" style={{ margin: '0 0 14px' }}>
              Если есть завершённая тренировка этой программы, перенесены её подходы.
              Если истории нет — сохранённые подходы шаблона, а для старых программ — план и пустой вес. Вес и повторы можно менять.
              Галочка — только личная отметка «сделал».
            </p>
          ) : null}

          {draft.exercises.map((exercise, exerciseIndex) => (
            <section className={"card workout-exercise "+(planned&&focused===exerciseIndex?'tp-current':'')} key={exercise.id}>
              <div className="row">
                <div className="exh">
                  <span className="exi" aria-hidden="true">
                    <img src={exercise.icon} alt="" />
                  </span>
                  <h3>
                    {exerciseIndex + 1}. {exercise.name}
                  </h3>
                </div>
                {isReady ? (
                  <span className="pill bl">
                    {actualSetSummary(exercise.sets)}
                  </span>
                ) : (
                  <button
                    className="b ic"
                    hidden={planned}
                    type="button"
                    onClick={() => removeExercise(exerciseIndex)}
                    aria-label="Удалить упражнение"
                  >
                    ×
                  </button>
                )}
              </div>

              {planned&&<p className="tp-actual-summary">{exercise.sets.length>0&&exercise.sets.every(s=>s.done)?'✓ ':''}{actualSetSummary(exercise.sets.filter(s=>s.weightKg!==''&&s.reps!==''))} заполнено</p>}
              {planned&&<button className="b tp-expand" aria-label={focused===exerciseIndex?'Свернуть':'Открыть упражнение'} aria-expanded={focused===exerciseIndex} onClick={()=>setFocused(focused===exerciseIndex?-1:exerciseIndex)}>{focused===exerciseIndex?'Свернуть':'Открыть упражнение'}</button>}
              <div hidden={planned&&focused!==exerciseIndex}>
              {planned&&<button className="b tp-remove-exercise" onClick={()=>removeExercise(exerciseIndex)}>Удалить упражнение</button>}
              {planMeta&&<PlanExerciseActions id={id} exerciseId={exercise.exerciseId.replace(/^plan:/,'')} allDone={exercise.sets.length>0&&exercise.sets.every(s=>s.done)} meta={planMeta} run={runPlanAction}/>}
              <span className="prev">
                {exercise.previous
                  ? `Прошлый раз · ${formatDayMonthShort(exercise.previous.date)}: ${
                      exercise.previous.weightKg ?? 0
                    } кг × ${exercise.previous.reps ?? 0}`
                  : 'Первый раз — прошлого результата нет'}
              </span>

              <div className="sh" aria-hidden="true">
                <span>#</span>
                <span style={{ textAlign: 'center' }}>Вес, кг</span>
                <span style={{ textAlign: 'center' }}>Повторы</span>
                <span>Сделал</span>
              </div>

              {exercise.sets.map((set, setIndex) => (
                <div
                  className={`set${set.done ? ' done' : ''}${swiped===set.id?' tp-swipe-open':''}`}
                  onPointerDown={planned?event=>{if((event.target as HTMLElement).closest('input,button,select'))return;swipeStart.current={id:set.id,x:event.clientX,y:event.clientY};event.currentTarget.setPointerCapture(event.pointerId)}:undefined}
                  onPointerUp={planned?event=>{const start=swipeStart.current;swipeStart.current=null;if(start?.id===set.id&&start.x-event.clientX>30&&Math.abs(start.y-event.clientY)<25)setSwiped(set.id)}:undefined}
                  onPointerCancel={()=>{swipeStart.current=null}}
                  onKeyDown={event=>{if(event.key==='Escape')setSwiped(null)}}
                  key={set.id}
                >
                  {swiped===set.id&&<button className="b d tp-swipe-delete" type="button" aria-label="Удалить смахнутый подход" onClick={event=>{event.currentTarget.closest('section')?.querySelector('button')?.focus();removeSet(exerciseIndex,setIndex);setSwiped(null)}}>Удалить</button>}
                  <span className="n">{setIndex + 1}</span>
                  <input
                    className="f mid"
                    inputMode="decimal"
                    aria-label={`Подход ${setIndex + 1}, вес`}
                    placeholder={planMeta?.prescription.exercises.find(e=>'plan:'+e.exerciseId===exercise.exerciseId)?.loadKg?.toString()??''}
                    readOnly={planned}
                    onKeyDown={planned?e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();e.currentTarget.click()}}:undefined}
                    onClick={planned?()=>setWheel({exercise:exerciseIndex,set:setIndex,field:'weightKg',value:set.weightKg,step:exercise.exerciseId.includes('dumbbell')?1:2.5}):undefined}
                    value={set.weightKg}
                    aria-invalid={!validNumber(set.weightKg, 1000)}
                    onChange={(event) =>
                      patchSet(exerciseIndex, setIndex, {
                        weightKg: event.target.value,
                      })
                    }
                  />
                  <input
                    className="f mid"
                    inputMode="numeric"
                    aria-label={`Подход ${setIndex + 1}, повторы`}
                    placeholder={planMeta?.prescription.exercises.find(e=>'plan:'+e.exerciseId===exercise.exerciseId)?.repMin?.toString()??''}
                    readOnly={planned}
                    onKeyDown={planned?e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();e.currentTarget.click()}}:undefined}
                    onClick={planned?()=>setWheel({exercise:exerciseIndex,set:setIndex,field:'reps',value:set.reps,step:1}):undefined}
                    value={set.reps}
                    aria-invalid={!validNumber(set.reps, 1000, true)}
                    onChange={(event) =>
                      patchSet(exerciseIndex, setIndex, {
                        reps: event.target.value,
                      })
                    }
                  />
                  {(
                    <button
                      className="tick"
                      type="button"
                      aria-label={set.done ? 'Отменить выполнение' : 'Отметить выполненным'}
                      aria-pressed={set.done}
                      onClick={() => patchSet(exerciseIndex, setIndex, { done: !set.done })}

                    >
                      <span aria-hidden="true">{set.done ? '✓' : ''}</span>
                    </button>
                  )}
                </div>
              ))}

              <button
                className="b"
                type="button"
                style={{ width: '100%', marginTop: 6 }}
                onClick={() => addSet(exerciseIndex)}
              >
                + Подход
              </button>

              <details className="set-removal">
                <summary>Удалить подход</summary>
                {exercise.sets.map((set, setIndex) => <button className="b" type="button" key={set.id}
                  onClick={(event) => {
                    event.currentTarget.closest('details')?.querySelector('summary')?.focus();
                    removeSet(exerciseIndex, setIndex);
                  }}>Удалить подход {setIndex + 1}</button>)}
              </details>

              {exercise.restSec ? (
                <p className="nt" style={{ margin: '8px 0 0' }}>
                  Отдых {exercise.restSec} с
                </p>
              ) : null}
              </div>
            </section>
          ))}

          {planned?<PlanAddExercise id={id} run={runPlanAction}/>:<button className="b dash" type="button" onClick={() => setPicker(true)}>
            + Добавить упражнение
          </button>}
        </>
      ) : null}

      {!numericValid ? <p className="err" role="alert">Проверьте числа: вес и расстояние — от 0 до 1000; повторы — целое от 0 до 1000; минуты — целое от 0 до 1440. Правки пока не сохранены.</p> : null}
      <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
        {isDone ? (
          <button
            className="b p big"
            type="button"
            onClick={() => void leave('/workouts/history')}
            disabled={busy || !numericValid}
          >
            Готово
          </button>
        ) : (
          <button className="b p big" type="button" onClick={() => void finish()} disabled={busy || !numericValid}>
            Завершить тренировку
          </button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {!isActivity ? (
            <button
              className="b"
              type="button"
              style={{ flexGrow: 1 }}
              onClick={() => void saveAsTemplate()}
              disabled={busy || !numericValid}
            >
              Сохранить как шаблон
            </button>
          ) : null}
          <button className="b d" type="button" onClick={() => setConfirmDelete(true)}>
            Удалить
          </button>
        </div>
      </div>
      </fieldset>

      {!planned&&<TabBar />}

      {picker && !busy ? (
        <ExercisePicker onPick={addExercise} onClose={() => setPicker(false)} />
      ) : null}

      {gymPicker && draft && !busy ? (
        <GymPicker
          currentId={draft.gymId || null}
          onPick={(gym) => {
            setGymPicker(false);
            update({ gymId: gym?.id ?? '', gymName: gym?.name ?? '' });
          }}
          onClose={() => setGymPicker(false)}
        />
      ) : null}

      {wheel&&<Dialog title={wheel.field==='weightKg'?'Вес, кг':'Повторения'} confirmLabel="Применить" onCancel={()=>setWheel(null)} onConfirm={()=>{patchSet(wheel.exercise,wheel.set,{[wheel.field]:wheel.value});setWheel(null)}}><select className="f tp-wheel" size={5} aria-label="Значение" onKeyDown={e=>{if(e.key==='Escape'){e.preventDefault();e.stopPropagation();setWheel(null)}}} value={wheel.value} onChange={e=>setWheel({...wheel,value:e.target.value})}><option value="">Не заполнено</option>{Array.from({length:Math.floor((wheel.field==='reps'?100:500)/wheel.step)+1},(_,i)=>i*wheel.step).map(v=><option key={v} value={v}>{v}</option>)}</select><label className="fl">Точное значение<input className="f" inputMode={wheel.field==='reps'?'numeric':'decimal'} value={wheel.value} onChange={e=>setWheel({...wheel,value:e.target.value})}/></label><p className="nt">Пустое поле — подход ещё не заполнен. Подсказка плана не становится выполненным результатом.</p></Dialog>}
      {confirmDelete ? (
        <Dialog
          title={isDone ? 'Удалить тренировку?' : 'Удалить черновик?'}
          confirmLabel="Удалить"
          danger
          busy={busy}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => void remove()}
        >
          <p>
            {isDone
              ? 'Обновятся минуты, объём и игровые очки. Остальные записи останутся.'
              : 'Черновик не попал в историю — удаление ни на что не повлияет.'}
          </p>
        </Dialog>
      ) : null}
    </Screen>
  );
}
